import React, { useState, useEffect } from "react";
import { fetchProfile, getCachedProfile } from "../api";

// Helper to nicely format the string
const formatStatsText = (text) => {
  if (!text) return null;
  let cleaned = text
    .replace(/[\n\t\r]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Normalize slash groups: "30 / 45 / 60" -> "30/45/60"
  cleaned = cleaned.replace(/\s*\/\s*/g, '/');

  // Keep this phrase on one line.
  cleaned = cleaned.replace(/Special Pricing\s*\(\s*Including room hire\s*\)/gi, 'Special Pricing (Including room hire)');

  // Insert readable breaks for common sections.
  cleaned = cleaned.replace(/\s+([A-Z]\s*Cup\b)/g, '\n$1');
  cleaned = cleaned.replace(/\s*(Special Pricing(?:\s*\(Including room hire\))?)/i, '\n$1');
  cleaned = cleaned.replace(/(Special Pricing(?:\s*\(Including room hire\))?)(\d{1,3}\/\d{1,3}\/\d{1,3})/i, '$1\n$2');
  cleaned = cleaned.replace(/(Special Pricing(?:\s*\(Including room hire\))?)\s+(\d{1,3}\/\d{1,3}\/\d{1,3})/i, '$1\n$2');
  cleaned = cleaned.replace(/(\d{1,3}\/\d{1,3}\/\d{1,3})(\d{1,3}\/\d{1,3}\/\d{1,3})/g, '$1\n$2');
  cleaned = cleaned.replace(/(\d{1,3}\/\d{1,3}\/\d{1,3})\s+(\d{1,3}\/\d{1,3}\/\d{1,3})/g, '$1\n$2');

  const lines = cleaned
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  // Convert merged name+nationality into "Name - Nationality" where possible.
  if (lines.length > 0 && !lines[0].includes(' - ')) {
    const nationalities = [
      'Japanese', 'Japan', 'Korean', 'Korea', 'Thai', 'Chinese', 'China',
      'Vietnamese', 'Vietnam', 'Taiwanese', 'Taiwan', 'Russian', 'European',
      'Eurasian', 'Indian', 'Brazilian', 'Indonesian', 'Turkish', 'Iranian',
      'Australian', 'Aussie', 'NZ', 'New Zealand'
    ];

    const natRegex = new RegExp(`^(.+?)\\s*(${nationalities.join('|')})$`, 'i');
    const match = lines[0].match(natRegex);
    if (match) {
      lines[0] = `${match[1].trim()} - ${match[2].trim()}`;
    }
  }

  return (
    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
      {lines.map((line, idx) => (
        <React.Fragment key={idx}>
          {line.trim()}<br/>
        </React.Fragment>
      ))}
    </div>
  );
};

// Helper to format the HTML description cleanly
const cleanDescriptionHTML = (htmlString) => {
  if (!htmlString) return { __html: "" };
  // A lot of raw scrapes have clunky inline styling. We can strip or space it out if needed.
  let cleanHTML = htmlString.replace(/style="[^"]*"/g, ''); // strip inline styles letting our CSS rule
  return { __html: cleanHTML };
};

const ProfileModal = ({ show, onHide, profileName, venue = "marrickville" }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && profileName) {
      const cachedProfile = getCachedProfile(profileName, venue);
      if (cachedProfile) {
        setProfileData(cachedProfile);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      // Fetch profile details from backend
      fetchProfile(profileName, venue)
        .then((data) => {
          setProfileData(data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    } else {
      setProfileData(null);
    }
  }, [show, profileName, venue]);

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onHide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [show, onHide]);

  // Handle outside click
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  const [expandedImage, setExpandedImage] = useState(null);

  if (!show) return null;

  // Render the expanded image overlay if an image is selected
  if (expandedImage) {
    return (
      <div 
        onClick={() => setExpandedImage(null)}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          cursor: 'zoom-out'
        }}
      >
        <button 
          onClick={() => setExpandedImage(null)}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          &times;
        </button>
        <img 
          src={expandedImage} 
          alt="Expanded Profile" 
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%', 
            objectFit: 'contain',
            borderRadius: '4px'
          }} 
        />
      </div>
    );
  }

  return (
    <div 
      onClick={handleOutsideClick}
      style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-lg)',
        overflow: 'hidden',
        border: '1px solid var(--border-color)',
      }}>
        {/* Header */}
        <div style={{ padding: '15px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--bg-secondary)' }}>
          <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>{profileName} Profile</h4>
          <button onClick={onHide} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', lineHeight: '1', color: 'var(--text-primary)' }}>&times;</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div className="text-center py-4" style={{ color: 'var(--text-primary)' }}>Loading profile details...</div>
          ) : error ? (
            <div className="alert alert-warning">{error}</div>
          ) : profileData ? (
            <div>
              {/* Stats/Prices Array */}
              <div style={{ marginBottom: '20px', backgroundColor: 'var(--bg-tertiary)', padding: '15px', borderRadius: '6px' }}>
                <h5 style={{ borderBottom: '2px solid var(--border-color)', paddingBottom: '5px', marginBottom: '10px', color: 'var(--text-primary)' }}>Details & Pricing</h5>
                {profileData.stats ? (
                   Array.isArray(profileData.stats) ? 
                     profileData.stats.map((p, i) => <p key={i} style={{ margin: '0 0 5px 0' }}>{p}</p>)
                     : formatStatsText(profileData.stats)
                ) : (
                  <p className="text-muted" style={{ color: 'var(--text-muted)' }}>No pricing or details found.</p>
                )}
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                <h5>About</h5>
                {profileData.description ? (
                  <div dangerouslySetInnerHTML={cleanDescriptionHTML(profileData.description)} style={{ lineHeight: '1.6' }} />
                ) : (
                  <p className="text-muted" style={{ color: 'var(--text-muted)' }}>No description available.</p>
                )}
              </div>
              
              {/* Images */}
              {profileData.image_url ? (
                <div>
                  <h5>Photos</h5>
                  <div style={{ borderRadius: '6px', overflow: 'hidden' }}>
                    <img 
                      src={profileData.image_url.startsWith('http') ? profileData.image_url : `/api${profileData.image_url}`} 
                      alt={`${profileName}`} 
                      style={{ width: '100%', height: 'auto', display: 'block', maxWidth: '400px', cursor: 'zoom-in' }} 
                      loading="lazy" 
                      onClick={() => setExpandedImage(profileData.image_url.startsWith('http') ? profileData.image_url : `/api${profileData.image_url}`)}
                    />
                  </div>
                </div>
              ) : profileData.images && profileData.images.length > 0 ? (
                <div>
                  <h5>Photos</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
                    {profileData.images.map((imgUrl, i) => {
                      const finalUrl = imgUrl.startsWith('http') ? imgUrl : `/api${imgUrl}`;
                      return (
                      <div key={i} style={{ borderRadius: '6px', overflow: 'hidden' }}>
                         <img 
                            src={finalUrl} 
                            alt={`${profileName} ${i+1}`} 
                            style={{ width: '100%', height: 'auto', display: 'block', cursor: 'zoom-in' }} 
                            loading="lazy" 
                            onClick={() => setExpandedImage(finalUrl)}
                          />
                      </div>
                    )})}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;